import React, { useState } from 'react';
import { Gift, Menu, X, ChevronDown } from 'lucide-react';
import ThemeSelector from '../ThemeSelector'; // ← Legg til theme selector

const Header = ({ currentPage, setCurrentPage, navigationItems, currentPath }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const handleNavigation = (pageId, url) => {
    setCurrentPage(pageId);
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  // Check if current page/path is active
  const isActiveItem = (item) => {
    if (item.url) {
      return currentPath === item.url;
    }
    return currentPage === item.id;
  };

  const isActiveSubItem = (subItem) => {
    return currentPath === subItem.url || currentPage === subItem.id;
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button
            onClick={() => handleNavigation('home', '/')}
            className="flex items-center space-x-2 text-xl font-bold text-secondary-800 hover:text-primary-600 transition-colors"
            aria-label="Gå til hjemmeside"
          >
            <Gift className="w-8 h-8 text-primary-500" />
            <span>Smartgave</span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8" role="navigation" aria-label="Hovednavigasjon">
            {navigationItems?.map((item) => (
              <div key={item.id} className="relative">
                {item.subItems ? (
                  <div className="relative">
                    <button
                      onClick={() => setOpenDropdown(openDropdown === item.id ? null : item.id)}
                      className={`flex items-center space-x-1 py-2 transition-colors ${
                        item.subItems.some(subItem => isActiveSubItem(subItem))
                          ? 'text-primary-600 font-medium'
                          : 'text-secondary-700 hover:text-primary-600'
                      }`}
                      aria-expanded={openDropdown === item.id}
                      aria-haspopup="true"
                    >
                      <span>{item.name}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    
                    {openDropdown === item.id && (
                      <div 
                        className="absolute top-full left-0 mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg py-2 min-w-48 z-50"
                        role="menu"
                      >
                        {item.subItems.map((subItem) => (
                          <button
                            key={subItem.id}
                            onClick={() => handleNavigation(subItem.id, subItem.url)}
                            className={`w-full text-left px-4 py-2 transition-colors hover:bg-primary-50 hover:text-primary-600 ${
                              isActiveSubItem(subItem)
                                ? 'text-primary-600 bg-primary-50 font-medium'
                                : 'text-secondary-700'
                            }`}
                            role="menuitem"
                          >
                            <span className="mr-2">{subItem.icon}</span>
                            {subItem.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavigation(item.id, item.url)}
                    className={`py-2 transition-colors ${
                      isActiveItem(item)
                        ? 'text-primary-600 font-medium'
                        : 'text-secondary-700 hover:text-primary-600'
                    }`}
                  >
                    {item.name}
                  </button>
                )}
              </div>
            ))}
          </nav>

          {/* Right side: Theme Selector + Mobile Menu */}
          <div className="flex items-center space-x-4">
            {/* Theme Selector - Desktop */}
            <div className="hidden lg:block">
              <ThemeSelector />
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-secondary-600 hover:text-secondary-800"
              aria-label={mobileMenuOpen ? 'Lukk meny' : 'Åpne meny'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-secondary-200 py-4">
            <nav className="space-y-2" role="navigation" aria-label="Mobil navigasjon">
              {navigationItems?.map((item) => (
                <div key={item.id}>
                  {item.subItems ? (
                    <div>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === item.id ? null : item.id)}
                        className={`w-full flex items-center justify-between px-4 py-2 transition-colors rounded-lg ${
                          item.subItems.some(subItem => isActiveSubItem(subItem))
                            ? 'text-primary-600 bg-primary-50 font-medium'
                            : 'text-secondary-700 hover:bg-primary-50 hover:text-primary-600'
                        }`}
                        aria-expanded={openDropdown === item.id}
                      >
                        <span>{item.name}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {openDropdown === item.id && (
                        <div className="mt-2 ml-4 space-y-1">
                          {item.subItems.map((subItem) => (
                            <button
                              key={subItem.id}
                              onClick={() => handleNavigation(subItem.id, subItem.url)}
                              className={`w-full text-left px-4 py-2 transition-colors rounded-lg ${
                                isActiveSubItem(subItem)
                                  ? 'text-primary-600 bg-primary-50 font-medium'
                                  : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                              }`}
                            >
                              <span className="mr-2">{subItem.icon}</span>
                              {subItem.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => handleNavigation(item.id, item.url)}
                      className={`w-full text-left px-4 py-2 transition-colors rounded-lg ${
                        isActiveItem(item)
                          ? 'text-primary-600 bg-primary-50 font-medium'
                          : 'text-secondary-700 hover:bg-primary-50 hover:text-primary-600'
                      }`}
                    >
                      {item.name}
                    </button>
                  )}
                </div>
              ))}

              {/* Theme Selector - Mobile */}
              <div className="mt-4 px-4">
                <div className="pt-4 border-t border-secondary-200">
                  <p className="text-sm font-medium text-secondary-600 mb-2">Fargetema:</p>
                  <ThemeSelector />
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;